// Repo API
import { apiClient } from './client';
import type {
  Collaborator,
  CreateRepoReq,
  ExploreReposResponse,
  PublicRepo,
  RepoList,
  SuggestReposResponse,
  UpdateRepoReq,
} from './types';

export const reposApi = {
  // 我自己的 repo 列表(需要 auth)
  async myRepos(): Promise<RepoList> {
    const { data } = await apiClient.get<RepoList>('/v1/user/repos');
    return data;
  },

  /** 公开浏览(Phase 4.2:走 /v1/explore/repos,支持 q 服务端检索 + 分页) */
  async explorePublic(params: {
    q?: string;
    limit?: number;
    offset?: number;
  } = {}): Promise<ExploreReposResponse> {
    const search = new URLSearchParams();
    if (params.q) search.set('q', params.q);
    if (params.limit !== undefined) search.set('limit', String(params.limit));
    if (params.offset !== undefined) search.set('offset', String(params.offset));
    const qs = search.toString();
    const { data } = await apiClient.get<ExploreReposResponse>(
      `/v1/explore/repos${qs ? `?${qs}` : ''}`,
    );
    return data;
  },

  /** 仓库名前缀补全:按仓库名前缀匹配公开仓库,返回待选项列表 */
  async suggestRepos(params: { q: string; limit?: number }): Promise<PublicRepo[]> {
    const search = new URLSearchParams();
    search.set('q', params.q);
    if (params.limit !== undefined) search.set('limit', String(params.limit));
    const { data } = await apiClient.get<SuggestReposResponse>(
      `/v1/explore/repos/suggest?${search.toString()}`,
    );
    return data.repos;
  },

  // 单个 repo 详情(公开 / 私有 — 私有需 auth)
  // ⚠️ 后端 RepoResponse 用 `#[serde(flatten)]`,返回的是直接 PublicRepo,
  //    不是 { repo: PublicRepo } 包装。
  async getRepo(owner: string, name: string): Promise<PublicRepo> {
    const { data } = await apiClient.get<PublicRepo>(
      `/v1/repos/${encodeURIComponent(owner)}/${encodeURIComponent(name)}`,
    );
    return data;
  },

  // 创建
  async create(payload: CreateRepoReq): Promise<PublicRepo> {
    const { data } = await apiClient.post<PublicRepo>('/v1/user/repos', payload);
    return data;
  },

  // 更新
  async update(
    owner: string,
    name: string,
    payload: UpdateRepoReq,
  ): Promise<PublicRepo> {
    const { data } = await apiClient.patch<PublicRepo>(
      `/v1/repos/${encodeURIComponent(owner)}/${encodeURIComponent(name)}`,
      payload,
    );
    return data;
  },

  // 删除
  async delete(owner: string, name: string): Promise<void> {
    await apiClient.delete(
      `/v1/repos/${encodeURIComponent(owner)}/${encodeURIComponent(name)}`,
    );
  },

  // 协作者:列表
  async listCollaborators(owner: string, name: string): Promise<Collaborator[]> {
    const { data } = await apiClient.get<Collaborator[]>(
      `/v1/repos/${encodeURIComponent(owner)}/${encodeURIComponent(name)}/collaborators`,
    );
    return data;
  },

  // 协作者:添加 / 修改角色
  async addCollaborator(
    owner: string,
    name: string,
    username: string,
    role: Collaborator['role'],
  ): Promise<void> {
    await apiClient.put(
      `/v1/repos/${encodeURIComponent(owner)}/${encodeURIComponent(name)}/collaborators/${encodeURIComponent(username)}`,
      { role },
    );
  },

  // 协作者:移除
  async removeCollaborator(
    owner: string,
    name: string,
    username: string,
  ): Promise<void> {
    await apiClient.delete(
      `/v1/repos/${encodeURIComponent(owner)}/${encodeURIComponent(name)}/collaborators/${encodeURIComponent(username)}`,
    );
  },
};

